import { shallowRef, watch, ref, type Ref } from 'vue';
import { get } from '../utils';
import {
  isFunction,
  isEmpty,
  map,
  reduce,
  extend,
  isUndefined,
} from 'lodash-es';
import { RowModel } from './row-model';
import { Pager } from './pager';
import { TableSorter, toggleSort } from './sort';
import { SlotRowModel } from './slot-row-model';
import { FooterTotalsRowModel } from './footer-totals-row-model';
import { RowGroupModel } from './row-group-model';
import { SaveEventArgs } from '../eventargs';
import type {
  IColumnDefinition,
  IDataTableHandler,
  Dictionary,
  TableOptions,
} from '../types';
import { normalizeColumns } from '../normalize-columns';

export class TableModel {
  public rows: RowModel[] = [];
  public view: Ref<any[]> = shallowRef([]);
  public editingRow: Ref<RowModel | null> = shallowRef(null);

  public commitPending = ref(false);

  public options: TableOptions;
  public pager: Pager;
  public sorter: TableSorter;

  private handler?: IDataTableHandler;

  private pendingRows: any[] | null = null;

  private updatingView = false;

  public VM_SPACER = new SlotRowModel(
    'spacer',
    {},
    { trClass: 'itp-datatable__spacer', tdClass: 'border-0' },
  );

  public VM_RULER = new SlotRowModel(
    'spacer',
    {},
    { trClass: 'itp-datatable__spacer', ruler: true },
  );

  public VM_AFTER_LAST_ROW!: SlotRowModel;

  constructor(options: TableOptions, handler?: IDataTableHandler) {
    options.columns = normalizeColumns(options.columns);
    this.options = options;
    this.handler = handler;

    this.VM_AFTER_LAST_ROW = new SlotRowModel(
      'afterLastRow',
      { table: this },
      { trClass: 'itp-datatable__close', tdClass: 'border-0' },
    );

    this.pager = new Pager({
      enabled: options.paging || false,
      totalSize: 0,
      pageSize: options.pageSize || 10,
    });

    this.sorter = new TableSorter();

    this.updateRows(options.rows || []);
    watch(
      [this.pager.currentPage, this.pager.pageSize, this.pager.enabled],
      () => this.updateView('pager'),
    );
  }

  public get isEditing() {
    return this.editingRow.value !== null;
  }

  public get hasGroups() {
    return !!(this.options.groups && this.options.groupKeyField);
  }

  public get canCreateRow() {
    return !!(this.options.editable && this.options.canCreateRow);
  }

  public get addSpacerRow() {
    return (
      this.options.rowDetailsMode === 'expandable' ||
      this.options.rowDetailsMode === 'alwaysVisible'
    );
  }

  public get selectedRows(): RowModel[] {
    return this.rows.filter((r) => r.isSelected.value);
  }

  public isRowEditable(row: RowModel): boolean {
    if (!this.options.editable || this.isEditing) {
      return false;
    }

    if (isFunction(this.options.isRowEditable)) {
      return this.options.isRowEditable(row.data);
    }

    return true;
  }

  public isRowDeletable(row: RowModel): boolean {
    if (!this.options.editable || this.isEditing) {
      return false;
    }

    if (isFunction(this.options.isRowDeletable)) {
      return this.options.isRowDeletable(row.data);
    }

    return true;
  }

  public getRowKey(data: any): string | number | null {
    const field = this.options.rowKeyField || 'id';

    return get(data, field, null);
  }

  public updateRows(rawRows: any[]) {
    if (this.isEditing || this.commitPending.value) {
      this.pendingRows = rawRows || [];
      return;
    }

    this.pendingRows = null;
    this.editingRow.value = null;
    this.rows = (rawRows || []).map(
      (inputData) => new RowModel(this, inputData),
    );
    this.updateView('updateRows');
  }

  private applyPendingRows() {
    if (
      this.pendingRows != null &&
      !this.isEditing &&
      !this.commitPending.value
    ) {
      const pending = this.pendingRows;
      this.pendingRows = null;
      this.updateRows(pending);
    }
  }

  public beginEdit(row: RowModel) {
    if (this.isEditing || this.commitPending.value) {
      return;
    }

    this.editingRow.value = row;
    row.onEnterEdit();
    this.updateView('beginEdit');
  }

  public addNewRow(group?: RowGroupModel) {
    if (this.isEditing || this.commitPending.value) {
      return;
    }

    const extras: Dictionary<any> = {};
    if (group && this.options.groupKeyField) {
      extras[this.options.groupKeyField] = isEmpty(group.id) ? null : group.id;
    }

    const newRow = new RowModel(this, this.getEmptyRow(extras));
    this.rows = [...this.rows, newRow];

    if (this.pager.enabled.value) {
      this.pager.currentPage.value =
        Math.ceil(this.rows.length / this.pager.pageSize.value) || 1;
    }

    this.beginEdit(newRow);
  }

  public endEdit(row: RowModel) {
    if (this.editingRow.value === row) {
      this.editingRow.value = null;
    }
    row.isEditing.value = false;
  }

  public cancelEdit(row: RowModel) {
    if (this.editingRow.value !== row || this.commitPending.value) {
      return;
    }

    const wasNew = row.isNew.value;

    row.onCancelEdit();
    this.editingRow.value = null;

    if (wasNew) {
      this.removeRow(row);
    } else {
      this.updateView('cancelEdit');
    }

    this.applyPendingRows();
  }

  public removeRow(row: RowModel) {
    this.rows = this.rows.filter((r) => r !== row);

    if (this.editingRow.value === row) {
      this.editingRow.value = null;
    }

    this.updateView('removeRow');
  }

  public sortRows(col: IColumnDefinition, multiSort = false) {
    if (!col.sortable || this.options.sortable === false) {
      return;
    }

    const newSort = toggleSort(col.sort || 0);
    col.sort = newSort;

    if (!multiSort) {
      this.options.columns.forEach((c) => {
        if (c !== col) {
          c.sort = 0;
        }
      });
      this.sorter.sortColumns.value = [];
    }

    this.sorter.updateSortColumn(col.field, newSort, multiSort);
    this.updateView('sortRows');
  }

  public setPaging(enabled: boolean) {
    this.pager.enabled.value = enabled;
  }

  public goToPage(pageNumber: number) {
    const maxPage =
      Math.ceil(this.pager.totalSize.value / this.pager.pageSize.value) || 1;

    this.pager.currentPage.value = Math.min(Math.max(1, pageNumber), maxPage);
  }

  public nextPage() {
    this.goToPage(this.pager.currentPage.value + 1);
  }

  public prevPage() {
    this.goToPage(this.pager.currentPage.value - 1);
  }

  public onRowSelectionChanged(row: RowModel) {
    if (this.handler) {
      this.handler.emit('selection-change', {
        selection: this.selectedRows,
        row,
        selected: row.isSelected.value,
      });
    }
  }

  public emitCellEvent(eventName: string, payload: any) {
    if (this.handler) {
      this.handler.emit(eventName, payload);
    }
  }

  public emitFocus(rowId: string | number | null, fieldName: string) {
    if (this.handler) {
      this.handler.emit('focus-cell', { row: rowId, fieldName });
    }
  }

  public async saveRow(row: RowModel): Promise<any> {
    if (!this.handler) {
      return undefined;
    }
    return this.handler.emit('save', new SaveEventArgs(row.data));
  }

  public async deleteRow(
    row: RowModel,
    options?: { skipConfirm?: boolean },
  ): Promise<boolean> {
    if (this.getRowKey(row.data) == null) {
      this.removeRow(row);
      return true;
    }

    if (!this.handler) {
      return false;
    }

    await this.handler.emit('delete', { row: row.data, ...options });
    this.removeRow(row);
    return true;
  }

  public commitEdit(row: RowModel) {
    this.endEdit(row);
    this.updateView('save');
  }

  public beginCommit() {
    this.commitPending.value = true;
  }

  public endCommit() {
    this.commitPending.value = false;
    this.applyPendingRows();
  }

  public *iterRows(group?: string | null): Generator<RowModel> {
    const gKey = this.hasGroups ? this.options.groupKeyField! : null;

    for (const row of this.rows) {
      if (group == null) {
        yield row;
        continue;
      }

      const groupKey = gKey ? get(row.data, gKey, '') : '';

      if (group === '' && (groupKey == null || groupKey === '')) {
        yield row;
        continue;
      }

      if (groupKey === group) {
        yield row;
      }
    }
  }

  private *generateView(rows: RowModel[]): Generator<any> {
    if (this.hasGroups) {
      const groupKeyField = this.options.groupKeyField!;
      const groups = [RowGroupModel.empty()].concat(
        map(this.options.groups, (g) => new RowGroupModel(g, [])),
      );
      const groupsByKey = reduce(
        groups,
        (m: Dictionary<any>, g) => {
          m[g.id] = g;
          return m;
        },
        {},
      );

      for (const row of rows) {
        const key = get(row.data, groupKeyField, null);
        if (key != null && groupsByKey[key]) {
          groupsByKey[key].rows.push(row);
        } else {
          groups[0].rows.push(row);
        }
      }

      if (this.addSpacerRow) {
        yield this.VM_SPACER;
      }

      for (const group of groups) {
        yield group;

        for (const row of group.rows) {
          if (this.addSpacerRow) {
            yield this.VM_SPACER;
          }
          yield row;
        }

        if (this.canCreateRow) {
          yield this.VM_SPACER;
          yield new AddRowToGroupModel(this, group);
        }

        if (this.options.footerTotals && group.rows.length > 0) {
          yield new FooterTotalsRowModel(this, group);
        }

        yield this.VM_RULER;
      }

      if (rows.length) {
        yield this.VM_AFTER_LAST_ROW;
      }

      return;
    }

    if (this.addSpacerRow) {
      yield this.VM_SPACER;
    }

    for (const row of rows) {
      yield row;
    }

    if (this.canCreateRow) {
      yield this.VM_SPACER;
      yield new AddRowModel(this);
    }

    if (rows.length) {
      yield this.VM_AFTER_LAST_ROW;
    }

    if (this.options.footerTotals && rows.length > 0) {
      yield new FooterTotalsRowModel(this);
    }
  }

  public updateView(_trigger?: string) {
    if (this.updatingView) {
      return;
    }

    this.updatingView = true;

    try {
      let rows = this.sorter.sortItems([...this.rows]) as RowModel[];

      this.pager.setTotalSize(rows.length);

      if (this.pager.enabled.value) {
        rows = this.pager.currentPageSlice(rows) as RowModel[];
      } else if (rows.length > 10000) {
        console.warn(
          'DataTable: Paging disabled with too many rows. Truncating to 10000.',
        );
        rows = rows.slice(0, 10000);
      }

      this.view.value = [...this.generateView(rows)];
    } finally {
      this.updatingView = false;
    }
  }

  private getEmptyRow(extras?: Dictionary<any>) {
    const result: Dictionary<any> = extend(
      {},
      this.options.rowTemplate,
      extras,
    );

    for (const column of this.options.columns) {
      if (!column.readonly) {
        const field = column.field || column.name;

        if (!isUndefined(column.default)) {
          result[field] = isFunction(column.default)
            ? column.default()
            : column.default;
          continue;
        }

        if (!(field in result)) {
          result[field] = null;
        }
      }
    }
    return result;
  }
}

export class AddRowModel {
  public readonly isAddRow: boolean = true;
  public readonly id = 'table__cmd_add';
  public readonly table: TableModel;

  constructor(table: TableModel) {
    this.table = table;
  }

  execute(_ev: Event) {
    this.table.addNewRow();
  }
}

export class AddRowToGroupModel {
  public readonly isAddRowToGroup: boolean = true;
  public readonly id: string;
  public readonly table: TableModel;
  public readonly group: RowGroupModel;

  constructor(table: TableModel, group: RowGroupModel) {
    this.table = table;
    this.group = group;
    this.id = `${group.id}__cmd_add`;
  }

  execute(_ev: Event) {
    this.table.addNewRow(this.group);
  }
}
